// Converts a video into a web-friendly MP4 (H.264 + AAC, index at the start, short
// side capped at 1080p) in the browser, so any browser can stream it. Raw uploads are
// often 1080p at ~18 Mbps, in codecs/containers browsers handle badly (HEVC, WMV) and
// with the index at the end of the file, which makes playback slow to start.
//
// What triggers work (each part is judged on its own):
//   video: not H.264, an H.264 profile browsers can't play, short side above 1080p, or a
//          file bitrate above REMUX_MAX_BITRATE -> re-encoded to H.264.
//   audio: not AAC -> encoded to AAC.
// Anything not triggered is copied untouched; the file is only re-wrapped as MP4.
//
// Mediabunny is imported lazily: it is only needed when uploading, and has to stay
// out of the public landing page bundle.

const MAX_SHORT_SIDE = 1080;
const VIDEO_BITRATE = 5_000_000; // at 1920x1080; smaller frames get less (see targetBitrate)
const REFERENCE_PIXELS = 1920 * 1080;
const MIN_VIDEO_BITRATE = 600_000;
const AUDIO_BITRATE = 128_000;
// A file already within these limits is only re-wrapped (index moved to the start),
// not re-encoded. Above the 5 Mbps video target (plus audio), so converted files
// always pass and are never converted again.
const REMUX_MAX_BITRATE = 8_000_000;

// Bitrate for a re-encoded video. Scaled by pixel count to the power 0.75 rather than
// linearly: smaller frames still need proportionally more bits per pixel
// (1080p: 5 Mbps, 720p: ~2.7 Mbps, 360p: ~1 Mbps).
const targetBitrate = (pixels) =>
    Math.round(Math.max(MIN_VIDEO_BITRATE, Math.min(VIDEO_BITRATE, VIDEO_BITRATE * (pixels / REFERENCE_PIXELS) ** 0.75)));
// H.264 profiles every browser can play: Baseline, Main and High (8-bit 4:2:0).
const WEB_AVC_PROFILE = /^avc[13]\.(42|4d|64)/i;

export const toMp4Name = (name) => `${name.replace(/\.[^./\\]+$/, '')}.mp4`;

const canceledError = () => Object.assign(new Error('Conversion cancelled'), { code: 'ERR_CANCELED' });

const cannotConvertError = (name, blocker) =>
    new Error(
        blocker
            ? `This browser can't convert "${name}": ${blocker}. Use Chrome or Edge, or convert the video to an MP4 (H.264) first.`
            : `This browser can't convert "${name}" for web playback. Use the latest Chrome or Edge, or convert the video to an MP4 (H.264) first.`
    );

const CODEC_LABEL = { avc: 'H.264', hevc: 'HEVC (H.265)', vp8: 'VP8', vp9: 'VP9', av1: 'AV1', prores: 'ProRes', aac: 'AAC', mp3: 'MP3', ac3: 'AC-3', eac3: 'E-AC-3', opus: 'Opus', vorbis: 'Vorbis', flac: 'FLAC' };
const codecLabel = (codec) => CODEC_LABEL[codec] || String(codec || 'unknown').toUpperCase();

// Converting needs the browser's own video tools (WebCodecs), and what they cover differs:
// Firefox, for one, can't decode HEVC and can't encode AAC. Returns why this browser can't
// convert the video, or null when it can.
const findBlocker = async (mediabunny, videoTrack, audioTrack, videoReasons, audioReasons) => {
    if (videoReasons.length > 0) {
        if (!(await videoTrack.canDecode())) return `${codecLabel(videoTrack.codec)} video can't be decoded by this browser`;
        if (!(await mediabunny.canEncodeVideo('avc'))) return "this browser can't encode H.264 video";
    }
    if (audioReasons.length > 0) {
        if (!(await audioTrack.canDecode())) return `${codecLabel(audioTrack.codec)} audio can't be decoded by this browser`;
        if (!(await mediabunny.canEncodeAudio('aac'))) return `the ${codecLabel(audioTrack.codec)} audio would need AAC encoding, which this browser can't do`;
    }
    return null; // nothing to encode or decode, or this browser can do it
};

// `file` (a File/Blob) or `url` is the source.
const openInput = async ({ file, url }) => {
    const mediabunny = await import('mediabunny');
    const { Input, BlobSource, UrlSource, ALL_FORMATS } = mediabunny;
    const input = new Input({ source: file ? new BlobSource(file) : new UrlSource(url), formats: ALL_FORMATS });
    return { mediabunny, input };
};

// Reads the video's tracks and works out whether it is already web-friendly.
const inspect = async (input, name, mediabunny) => {
    let videoTrack;
    try {
        videoTrack = await input.getPrimaryVideoTrack();
    } catch (error) {
        throw new Error(`"${name}" isn't a supported video file. Supported video formats: MP4, MOV.`);
    }
    if (!videoTrack) throw new Error(`"${name}" doesn't contain a video track.`);
    const audioTrack = await input.getPrimaryAudioTrack();

    const width = videoTrack.displayWidth;
    const height = videoTrack.displayHeight;
    const shortSide = Math.min(width, height);
    const durationS = await input.computeDuration();
    const sizeBytes = await input.source.getSize();
    const bitrate = sizeBytes && durationS ? (sizeBytes * 8) / durationS : Infinity;
    const avcProfile = (await videoTrack.getCodecParameterString().catch(() => null)) || '';

    const videoReasons = [];
    if (videoTrack.codec !== 'avc') videoReasons.push(`${codecLabel(videoTrack.codec)} video`);
    else if (!WEB_AVC_PROFILE.test(avcProfile)) videoReasons.push('H.264 profile not supported by browsers');
    if (shortSide > MAX_SHORT_SIDE) videoReasons.push(`${width}x${height}`);
    if (bitrate > REMUX_MAX_BITRATE) videoReasons.push(`${(bitrate / 1e6).toFixed(1)} Mbps`);
    const audioReasons = audioTrack && audioTrack.codec !== 'aac' ? [`${codecLabel(audioTrack.codec)} audio`] : [];
    const reasons = [...videoReasons, ...audioReasons];

    const blocker = await findBlocker(mediabunny, videoTrack, audioTrack, videoReasons, audioReasons);

    return { videoTrack, audioTrack, width, height, shortSide, durationS, sizeBytes, bitrate, reasons, videoReasons, audioReasons, blocker };
};

// Checks a video (by URL) without converting it. `needsConversion` is false when it is
// already small and in a format every browser plays. `blocker` says why this browser
// can't convert it (when it needs converting).
export const analyzeVideo = async ({ url, name }) => {
    const { mediabunny, input } = await openInput({ url });
    try {
        const { width, height, durationS, sizeBytes, bitrate, reasons, blocker } = await inspect(input, name, mediabunny);
        return { needsConversion: reasons.length > 0, reasons, blocker, width, height, durationS, sizeBytes, mbps: bitrate / 1e6 };
    } finally {
        input.dispose();
    }
};

// Resolves with a File named "<name>.mp4".
export const convertVideoForWeb = async ({ file, url, name = file?.name, signal, onProgress }) => {
    if (signal?.aborted) throw canceledError();
    onProgress?.(0);

    const { mediabunny, input } = await openInput({ file, url });
    const { Output, Conversion, ConversionCanceledError, BufferTarget, Mp4OutputFormat } = mediabunny;
    try {
        const { width, height, shortSide, videoReasons, audioReasons, blocker } = await inspect(input, name, mediabunny);
        if (blocker) throw cannotConvertError(name, blocker);

        // Left undefined, a track is copied untouched.
        let video;
        if (videoReasons.length > 0) {
            // Only one side is given so the other follows the aspect ratio.
            const resize = shortSide > MAX_SHORT_SIDE ? (width <= height ? { width: MAX_SHORT_SIDE } : { height: MAX_SHORT_SIDE }) : {};
            const outPixels = shortSide > MAX_SHORT_SIDE ? (width / shortSide) * (height / shortSide) * MAX_SHORT_SIDE ** 2 : width * height;
            video = { ...resize, codec: 'avc', forceTranscode: true, bitrate: targetBitrate(outPixels) };
        }
        const audio = audioReasons.length > 0 ? { codec: 'aac', bitrate: AUDIO_BITRATE } : undefined;

        const output = new Output({
            format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
            target: new BufferTarget(),
        });
        const conversion = await Conversion.init({ input, output, video, audio, showWarnings: false });

        const lostTrack = conversion.discardedTracks.some(
            (discarded) => discarded.reason !== 'discarded_by_user' && discarded.track.type !== 'subtitle'
        );
        if (!conversion.isValid || lostTrack) throw cannotConvertError(name);

        conversion.onProgress = (progress) => onProgress?.(Math.round(progress * 100));
        const onAbort = () => conversion.cancel();
        signal?.addEventListener('abort', onAbort);
        try {
            if (signal?.aborted) throw canceledError();
            await conversion.execute();
        } catch (error) {
            if (error instanceof ConversionCanceledError || signal?.aborted) throw canceledError();
            throw error;
        } finally {
            signal?.removeEventListener('abort', onAbort);
        }

        onProgress?.(100);
        return new File([output.target.buffer], toMp4Name(name), { type: 'video/mp4' });
    } finally {
        input.dispose();
    }
};
